import { useMutation } from 'react-query';
import { MutationConfig } from 'react-query/types/core/types';
import reactQueryConfig from '../config/react-query-config';

export type TAsyncStatesHandlers<TResult, TError> = {
    onSuccess?: (arg0?: TResult) => void;
    onError?: (arg0?: TError) => void;
    onStart?: () => void;
};

export type TMutationHookWrapper<TResult, TError, TVariables> = {
    mutationFn: (args0: TVariables) => Promise<TResult>;
    mutationConfig?: MutationConfig<TResult, TError, TVariables>;
    asyncStateHandlers?: TAsyncStatesHandlers<TResult, TError>;
};

/** Wrap mutation hook of React Query library with reaction on default async state changes: start, success, error */
const useMutationHookWrapper = <TResponse, TError, TVariables>({
    mutationFn,
    mutationConfig,
    asyncStateHandlers: { onSuccess, onError, onStart } = {},
}: TMutationHookWrapper<TResponse, TError, TVariables>) => {
    return useMutation<TResponse, TError, TVariables>(
        (variables) => {
            onStart && onStart();
            return mutationFn(variables);
        },
        {
            ...reactQueryConfig,
            ...mutationConfig,
            onSuccess: (data, variables) => {
                mutationConfig?.onSuccess &&
                    mutationConfig.onSuccess(data, variables);
                onSuccess && onSuccess(data);
            },
            onError: (error, variables, snapshotValue) => {
                mutationConfig?.onError &&
                    mutationConfig.onError(error, variables, snapshotValue);
                onError && onError(error);
            },
        },
    );
};

export default useMutationHookWrapper;
